import React from "react";
import { X, Minus, Download, Upload, RotateCcw, ChevronDown } from "lucide-react";
import { colors, fonts, shadows } from "../theme";
import { useStudio } from "../store/useStudio";
import { energyApi } from "../api/energy";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Energy resource id -> operational-profile source key.
const SOURCE_BY_RESOURCE = {
  ventilation_exhaust_air_recovered_heat: "ventilation",
  drain_water_heat_recovery_recovered_heat_new_in_tool: "hvac",
  anaerobic_digester_biogas: "biogas",
  solar_thermal_hot_water_heat: "solar_thermal",
  solar_pv_array_electricity: "pv",
};
export const isEnergyResource = (id) => id in SOURCE_BY_RESOURCE;

const SOURCE_ORDER = ["ventilation", "hvac", "biogas", "solar_thermal", "pv"];
const SOURCE_COLORS = { thermal: "#f88528", renewable: "#3bbbc2" };

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

export default function EnergyProfilePanel() {
  const panel = useStudio((s) => s.energyPanel);
  const geo = useStudio((s) => s.geo);
  const month = useStudio((s) => s.energyMonth);
  const params = useStudio((s) => s.energyParams);
  const normals = useStudio((s) => s.energyClimateMonths);
  const override = useStudio((s) => s.energyOverride);
  const setMonth = useStudio((s) => s.setEnergyMonth);
  const setParam = useStudio((s) => s.setEnergyParam);
  const setNormals = useStudio((s) => s.setEnergyClimateMonths);
  const setOverride = useStudio((s) => s.setEnergyOverride);
  const clearOverride = useStudio((s) => s.clearEnergyOverride);
  const resetParams = useStudio((s) => s.resetEnergyParams);
  const minimize = useStudio((s) => s.minimizeEnergyPanel);
  const close = useStudio((s) => s.closeEnergyPanel);

  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const fileRef = React.useRef(null);
  const focusSource = panel.source ? SOURCE_BY_RESOURCE[panel.source] : null;

  const monthNormal = React.useMemo(
    () => (normals?.months || []).find((m) => m.month === month) || null,
    [normals, month]
  );
  const climEdit = getPath(params, `climateByMonth.${month}`) || {};
  // Illustrative Northern-hemisphere seasonal shape used ONLY until real site normals load.
  // Fully editable and clearly labelled — replaced by Open-Meteo normals once a site is pinned.
  const fallback = React.useMemo(() => ({
    avg_temp_c: Math.round((10 - 12 * Math.cos((2 * Math.PI * month) / 12)) * 10) / 10,
    ghi_wh_m2_day: Math.round(3000 - 2500 * Math.cos((2 * Math.PI * month) / 12)),
  }), [month]);
  const climate = React.useMemo(() => ({
    avg_temp_c: climEdit.avg_temp_c ?? monthNormal?.avg_temp_c ?? fallback.avg_temp_c,
    ghi_wh_m2_day: climEdit.ghi_wh_m2_day ?? monthNormal?.ghi_wh_m2_day ?? fallback.ghi_wh_m2_day,
    lat: geo?.lat ?? 45,
  }), [climEdit.avg_temp_c, climEdit.ghi_wh_m2_day, monthNormal, fallback, geo]);

  // Fetch honest monthly climate normals once when the site is known.
  React.useEffect(() => {
    if (!panel.source || normals || !geo?.lat) return;
    energyApi.climateNormals(geo.lat, geo.lng).then(setNormals).catch(() => {});
  }, [panel.source, normals, geo, setNormals]);

  // Recompute the profile (debounced) whenever inputs change.
  React.useEffect(() => {
    if (!panel.source) return;
    let alive = true;
    setLoading(true);
    const body = {
      month,
      climate,
      building: params.building || {},
      diurnal_swing_c: params.diurnal_swing_c,
      op_window: params.op_window || {},
      sources: params.sources || {},
      hourly_override: override || undefined,
    };
    const t = setTimeout(() => {
      energyApi.profile(body)
        .then((d) => { if (alive) setProfile(d); })
        .catch(() => {})
        .finally(() => { if (alive) setLoading(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [panel.source, month, climate, params, override]);

  if (!panel.source) return null;

  const val = (path) => getPath(params, path) ?? getPath(profile?.params, path) ?? "";
  const num = (path, v) => setParam(path, v === "" ? "" : Number(v));
  const setClim = (key, v) => setParam(`climateByMonth.${month}.${key}`, v === "" ? "" : Number(v));

  const download = async () => {
    setMsg(null);
    try {
      const blob = await energyApi.exportXlsx({ month, climate, building: params.building || {}, diurnal_swing_c: params.diurnal_swing_c, op_window: params.op_window || {}, sources: params.sources || {}, hourly_override: override || undefined });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `energy_profile_${MONTHS[month]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setMsg("Could not export the workbook."); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMsg(null);
    try {
      const d = await energyApi.importXlsx(f);
      setOverride(d.hourly_override);
      setMsg(`Loaded ${d.rows} hourly rows from your workbook. Curves now use your data.`);
    } catch { setMsg("Could not read that workbook — expected 24 hourly rows."); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  if (panel.minimized) {
    return (
      <button style={styles.minPill} data-testid="energy-panel-minimized" onClick={() => minimize(false)}>
        <span style={styles.minDot} />
        Operational profile · {profile ? `${profile.avg.saving_pct}% met` : "…"}
      </button>
    );
  }

  return (
    <div style={styles.wrap} data-testid="energy-profile-panel">
      <div style={styles.head}>
        <div>
          <div style={styles.title}>Operational Energy Profile</div>
          <div style={styles.sub}>Day / night · summer / winter · how the average is built</div>
        </div>
        <div style={styles.headBtns}>
          <button style={styles.hBtn} data-testid="energy-panel-min" title="Minimize" onClick={() => minimize(true)}><Minus size={15} /></button>
          <button style={styles.hBtn} data-testid="energy-panel-close" title="Close" onClick={close}><X size={15} /></button>
        </div>
      </div>

      <div style={styles.body}>
        {/* Month selector */}
        <div style={styles.monthRow} data-testid="energy-month-row">
          {MONTHS.map((m, i) => (
            <button key={m} data-testid={`energy-month-${i}`} onClick={() => setMonth(i)}
              style={{ ...styles.monthBtn, ...(i === month ? styles.monthBtnOn : {}) }}>{m}</button>
          ))}
        </div>

        {/* Simplified average summary */}
        <div style={styles.summary} data-testid="energy-summary">
          <Stat label="Avg demand" value={profile ? kW(profile.avg.demand_w) : "—"} unit="kW" />
          <Stat label="Avg generated" value={profile ? kW(profile.avg.generated_w) : "—"} unit="kW" accent={SOURCE_COLORS.renewable} />
          <Stat label="Delivered" value={profile ? kW(profile.avg.delivered_w) : "—"} unit="kW" />
          <Stat label="Demand met" value={profile ? profile.avg.saving_pct : "—"} unit="%" accent="#00B26A" testId="energy-saving" />
        </div>
        <div style={styles.explain}>
          The simplified card number is the <b>daily average</b> of 24 hourly balances for {MONTHS[month]}. Because heating demand
          peaks at night while solar peaks at midday — and waste heat only flows while the building runs — supply and demand rarely
          line up. The chart shows the two as mirror-imaged curves; only the overlap is actually deliverable.
        </div>

        {/* Reversed bell-curve chart */}
        <ProfileChart profile={profile} loading={loading} />

        {override && (
          <div style={styles.overrideNote} data-testid="energy-override-note">
            Using your uploaded hourly workbook. <button style={styles.linkBtn} data-testid="energy-clear-override" onClick={clearOverride}>Revert to derived profile</button>
          </div>
        )}

        {/* Excel round-trip */}
        <div style={styles.actions}>
          <button style={styles.actBtn} data-testid="energy-download" onClick={download}><Download size={13} /> Download Excel</button>
          <button style={styles.actBtn} data-testid="energy-upload" onClick={() => fileRef.current?.click()}><Upload size={13} /> Upload edited</button>
          <button style={styles.actBtnGhost} data-testid="energy-reset" onClick={resetParams}><RotateCcw size={12} /> Reset</button>
          <input ref={fileRef} type="file" accept=".xlsx" style={{ display: "none" }} onChange={upload} data-testid="energy-upload-input" />
        </div>
        {msg && <div style={styles.msg} data-testid="energy-msg">{msg}</div>}

        {/* Editable climate + envelope */}
        <Group title={`Climate — ${MONTHS[month]}`} testId="energy-grp-climate" defaultOpen>
          <Field label="Avg outdoor T" unit="°C" value={climEdit.avg_temp_c ?? monthNormal?.avg_temp_c ?? fallback.avg_temp_c} placeholder={monthNormal?.avg_temp_c ?? fallback.avg_temp_c} onChange={(v) => setClim("avg_temp_c", v)} testId="energy-avg-temp" />
          <Field label="Daily solar (GHI)" unit="Wh/m²" value={climEdit.ghi_wh_m2_day ?? monthNormal?.ghi_wh_m2_day ?? fallback.ghi_wh_m2_day} placeholder={monthNormal?.ghi_wh_m2_day ?? fallback.ghi_wh_m2_day} onChange={(v) => setClim("ghi_wh_m2_day", v)} testId="energy-ghi" />
          <Field label="Day/night swing" unit="°C" value={val("diurnal_swing_c")} onChange={(v) => num("diurnal_swing_c", v)} testId="energy-swing" />
          <div style={styles.srcNote}>{normals ? "Pre-filled from Open-Meteo monthly normals for your site." : geo?.lat ? "Fetching site normals…" : "Illustrative seasonal values — pin your site for real normals, or edit below."}</div>
        </Group>

        <Group title="Building envelope" testId="energy-grp-envelope">
          <Field label="Footprint" unit="m²" value={val("building.footprint_m2")} onChange={(v) => num("building.footprint_m2", v)} />
          <Field label="Envelope area" unit="m²" value={val("building.envelope_area_m2")} onChange={(v) => num("building.envelope_area_m2", v)} />
          <Field label="U-value" unit="W/m²K" value={val("building.u_value")} onChange={(v) => num("building.u_value", v)} />
          <Field label="SHGC" unit="" value={val("building.shgc")} onChange={(v) => num("building.shgc", v)} />
          <Field label="Set-point" unit="°C" value={val("building.setpoint_c")} onChange={(v) => num("building.setpoint_c", v)} />
          <Field label="Operates from" unit="h" value={val("op_window.start")} onChange={(v) => num("op_window.start", v)} />
          <Field label="Operates until" unit="h" value={val("op_window.end")} onChange={(v) => num("op_window.end", v)} />
          <div style={styles.srcNote}>Demand = U × area × ΔT − GHI × footprint × SHGC. Ventilation & HVAC waste heat is only available inside the operating window.</div>
        </Group>

        {/* Per-source editable params + equation + contribution */}
        {SOURCE_ORDER.map((sk) => (
          <SourceGroup key={sk} sk={sk} profile={profile} val={val} num={num} focus={focusSource === sk} />
        ))}
      </div>
    </div>
  );
}

function SourceGroup({ sk, profile, val, num, focus }) {
  const setParam = useStudio((s) => s.setEnergyParam);
  const meta = profile?.meta?.[sk];
  const contrib = profile?.source_avg_w?.[sk];
  const isRenewable = sk === "solar_thermal" || sk === "pv";
  const accent = isRenewable ? SOURCE_COLORS.renewable : SOURCE_COLORS.thermal;
  const enabled = val(`sources.${sk}.enabled`);
  const fields = {
    ventilation: [["Ventilation flow", "L/s", "flow_l_s"], ["Return-air T", "°C", "return_air_c"]],
    hvac: [["Cooling capacity", "tons", "tons"], ["Heat-rejection factor", "×", "hrf"]],
    biogas: [["Digester output", "kW", "output_kw"], ["Availability", "×", "availability"]],
    solar_thermal: [["Collector area", "m²", "area_m2"], ["Efficiency", "η", "eff"]],
    pv: [["PV area", "m²", "area_m2"], ["System efficiency", "η", "eff"]],
  }[sk];
  return (
    <Group title={meta?.label || sk} testId={`energy-grp-${sk}`} accent={accent} defaultOpen={focus} highlight={focus}
      right={contrib != null ? `${kW(contrib)} kW avg` : null}>
      <div style={styles.eqRow} data-testid={`energy-eq-${sk}`}>{meta?.equation}</div>
      <div style={styles.srcNote}>{meta?.note}</div>
      <label style={styles.toggleRow}>
        <input type="checkbox" checked={enabled !== 0 && enabled !== false} data-testid={`energy-enable-${sk}`}
          onChange={(e) => setParam(`sources.${sk}.enabled`, e.target.checked)} />
        <span>Include this source</span>
      </label>
      {fields.map(([label, unit, key]) => (
        <Field key={key} label={label} unit={unit} value={val(`sources.${sk}.${key}`)} onChange={(v) => num(`sources.${sk}.${key}`, v)} testId={`energy-${sk}-${key}`} />
      ))}
    </Group>
  );
}

function ProfileChart({ profile, loading }) {
  const W = 300, H = 150, pad = 4;
  if (!profile) return <div style={styles.chartEmpty}>{loading ? "Computing…" : "No data"}</div>;
  const used = profile.demand_w, gen = profile.generated_total_w;
  const max = Math.max(1, ...used, ...gen);
  const x = (h) => pad + (h / 23) * (W - 2 * pad);
  const y = (v) => (H - 16) - (v / max) * (H - 24);
  const path = (arr) => arr.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = (arr) => `${path(arr)} L${x(23).toFixed(1)},${(H - 16).toFixed(1)} L${x(0).toFixed(1)},${(H - 16).toFixed(1)} Z`;
  return (
    <div style={styles.chartWrap} data-testid="energy-chart">
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
        <path d={area(used)} fill="rgba(248,133,40,0.12)" />
        <path d={area(gen)} fill="rgba(59,187,194,0.14)" />
        <path d={path(used)} fill="none" stroke={SOURCE_COLORS.thermal} strokeWidth="2" />
        <path d={path(gen)} fill="none" stroke={SOURCE_COLORS.renewable} strokeWidth="2" />
        {[0, 6, 12, 18, 23].map((h) => (
          <text key={h} x={x(h)} y={H - 3} fontSize="7" fill={colors.textTertiary} textAnchor="middle">{h}:00</text>
        ))}
      </svg>
      <div style={styles.legend}>
        <span style={styles.legItem}><span style={{ ...styles.legDot, background: SOURCE_COLORS.thermal }} /> Used (heating demand)</span>
        <span style={styles.legItem}><span style={{ ...styles.legDot, background: SOURCE_COLORS.renewable }} /> Generated (supply)</span>
      </div>
    </div>
  );
}

function Group({ title, right, accent = colors.textTertiary, testId, defaultOpen = false, highlight = false, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  React.useEffect(() => { if (highlight) setOpen(true); }, [highlight]);
  return (
    <section style={{ ...styles.group, ...(highlight ? { border: `1px solid ${accent}`, boxShadow: `0 0 0 2px ${accent}22` } : {}) }} data-testid={testId}>
      <button style={styles.groupHead} onClick={() => setOpen((v) => !v)} data-testid={`${testId}-toggle`}>
        <span style={{ ...styles.groupDot, background: accent }} />
        <span style={styles.groupTitle}>{title}</span>
        {right && <span style={styles.groupRight}>{right}</span>}
        <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", color: colors.textTertiary }} />
      </button>
      {open && <div style={styles.groupBody}>{children}</div>}
    </section>
  );
}

function Field({ label, unit, value, placeholder, onChange, testId }) {
  return (
    <div style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <input type="number" value={value} placeholder={placeholder != null ? String(placeholder) : ""}
        data-testid={testId} style={styles.fieldInput}
        onChange={(e) => onChange(e.target.value)} onClick={(e) => e.stopPropagation()} />
      {unit ? <span style={styles.fieldUnit}>{unit}</span> : null}
    </div>
  );
}

function Stat({ label, value, unit, accent, testId }) {
  return (
    <div style={styles.stat} data-testid={testId}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statVal, ...(accent ? { color: accent } : {}) }}>{value}<span style={styles.statUnit}>{unit}</span></div>
    </div>
  );
}

const kW = (w) => (Math.round((Number(w) || 0) / 100) / 10).toLocaleString();

const styles = {
  wrap: { position: "fixed", right: 20, bottom: 150, width: 380, maxHeight: "78vh", display: "flex", flexDirection: "column", background: colors.surface, borderRadius: 18, border: `1px solid ${colors.border}`, boxShadow: shadows.float || "0 24px 70px rgba(0,0,0,0.28)", zIndex: 70, overflow: "hidden", fontFamily: fonts.sans },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 16px", borderBottom: `1px solid ${colors.border}`, background: "rgba(0,0,0,0.015)" },
  title: { fontFamily: fonts.sans, fontSize: 14, fontWeight: 700, color: colors.textPrimary, letterSpacing: -0.2 },
  sub: { fontFamily: fonts.sans, fontSize: 10.5, color: colors.textTertiary, marginTop: 2 },
  headBtns: { display: "flex", gap: 6 },
  hBtn: { width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textSecondary, cursor: "pointer" },
  body: { padding: "12px 16px 18px", overflowY: "auto" },
  monthRow: { display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 12 },
  monthBtn: { flex: "1 0 12%", padding: "4px 0", borderRadius: 7, border: `1px solid ${colors.border}`, background: "#fff", fontFamily: fonts.mono, fontSize: 9.5, fontWeight: 700, color: colors.textSecondary, cursor: "pointer" },
  monthBtnOn: { background: colors.textPrimary, color: "#fff", borderColor: colors.textPrimary },
  summary: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 },
  stat: { padding: "8px 6px", borderRadius: 10, background: colors.canvas, border: `1px solid ${colors.border}`, textAlign: "center" },
  statLabel: { fontFamily: fonts.mono, fontSize: 7.5, letterSpacing: 0.4, color: colors.textTertiary, textTransform: "uppercase" },
  statVal: { fontFamily: fonts.sans, fontSize: 16, fontWeight: 700, color: colors.textPrimary, marginTop: 3, display: "flex", alignItems: "baseline", justifyContent: "center" },
  statUnit: { fontSize: 8.5, fontWeight: 500, color: colors.textTertiary, marginLeft: 2 },
  explain: { fontFamily: fonts.sans, fontSize: 11, lineHeight: 1.5, color: colors.textSecondary, marginBottom: 10 },
  chartWrap: { border: `1px solid ${colors.border}`, borderRadius: 12, padding: "8px 8px 4px", background: "#fff" },
  chartEmpty: { height: 150, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.sans, fontSize: 12, color: colors.textTertiary, border: `1px dashed ${colors.border}`, borderRadius: 12 },
  legend: { display: "flex", gap: 14, justifyContent: "center", marginTop: 4 },
  legItem: { display: "flex", alignItems: "center", gap: 5, fontFamily: fonts.sans, fontSize: 10, color: colors.textSecondary },
  legDot: { width: 9, height: 3, borderRadius: 2 },
  overrideNote: { marginTop: 8, fontFamily: fonts.sans, fontSize: 10.5, color: colors.textSecondary, background: "rgba(59,187,194,0.08)", border: "1px solid rgba(59,187,194,0.3)", borderRadius: 9, padding: "7px 9px" },
  linkBtn: { border: "none", background: "transparent", color: colors.brandDeep || "#2E86D9", fontWeight: 600, cursor: "pointer", padding: 0, fontSize: 10.5 },
  actions: { display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" },
  actBtn: { flex: 1, minWidth: 110, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 8px", borderRadius: 999, border: `1px solid ${colors.borderStrong}`, background: "#fff", fontFamily: fonts.sans, fontSize: 11, fontWeight: 600, color: colors.textSecondary, cursor: "pointer" },
  actBtnGhost: { display: "flex", alignItems: "center", gap: 5, padding: "7px 10px", borderRadius: 999, border: "none", background: "transparent", fontFamily: fonts.sans, fontSize: 11, fontWeight: 600, color: colors.textTertiary, cursor: "pointer" },
  msg: { marginTop: 8, fontFamily: fonts.sans, fontSize: 10.5, color: colors.textSecondary, lineHeight: 1.4 },
  group: { marginTop: 10, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden", background: "#fff" },
  groupHead: { width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left" },
  groupDot: { width: 8, height: 8, borderRadius: 999, flexShrink: 0 },
  groupTitle: { fontFamily: fonts.sans, fontSize: 12, fontWeight: 700, color: colors.textPrimary, flex: 1 },
  groupRight: { fontFamily: fonts.mono, fontSize: 10, fontWeight: 700, color: colors.textSecondary },
  groupBody: { padding: "4px 11px 11px", borderTop: `1px solid ${colors.border}` },
  eqRow: { fontFamily: fonts.mono, fontSize: 10.5, color: colors.textPrimary, background: colors.canvas, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "6px 9px", margin: "8px 0 6px", lineHeight: 1.4 },
  srcNote: { fontFamily: fonts.sans, fontSize: 10.5, lineHeight: 1.45, color: colors.textTertiary, margin: "4px 0 8px" },
  toggleRow: { display: "flex", alignItems: "center", gap: 7, fontFamily: fonts.sans, fontSize: 11.5, color: colors.textSecondary, margin: "2px 0 8px", cursor: "pointer" },
  field: { display: "flex", alignItems: "center", gap: 8, padding: "3px 0" },
  fieldLabel: { fontFamily: fonts.sans, fontSize: 11.5, color: colors.textSecondary, flex: 1 },
  fieldInput: { width: 82, padding: "5px 8px", borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: "#fff", fontFamily: fonts.mono, fontSize: 11.5, fontWeight: 700, color: colors.textPrimary, textAlign: "right" },
  fieldUnit: { fontFamily: fonts.mono, fontSize: 9, color: colors.textTertiary, width: 42 },
  minPill: { position: "fixed", right: 20, bottom: 150, zIndex: 70, display: "flex", alignItems: "center", gap: 8, padding: "9px 15px", borderRadius: 999, border: `1px solid ${colors.border}`, background: colors.surface, boxShadow: shadows.float || "0 12px 30px rgba(0,0,0,0.2)", fontFamily: fonts.sans, fontSize: 12, fontWeight: 600, color: colors.textPrimary, cursor: "pointer" },
  minDot: { width: 8, height: 8, borderRadius: 999, background: "#00B26A" },
};
